import table from './data/chiron.json';
import { norm360 } from './zodiac';

/**
 * Chiron is not in astronomy-engine, so its longitude comes from a table built from JPL Horizons
 * (scripts/build-chiron.js): apparent geocentric ecliptic longitude of date, one value every few days
 * from 1900 to 2050. Between rows a four-point cubic is exact to well under an arcminute.
 */
const START_MS = Date.parse(table.start);
const STEP_MS = table.stepDays * 86_400_000;
const LON = table.lon as number[];

/** Longitudes unwrapped so that 359° to 1° reads as a smooth step, not a jump. */
const unwrapped: number[] = (() => {
    const out: number[] = [];
    let offset = 0;
    LON.forEach((v, i) => {
        const deg = v * table.unit;
        if (i > 0) {
            const prev = out[i - 1] - offset;
            if (deg - prev < -180) offset += 360;
            else if (deg - prev > 180) offset -= 360;
        }
        out.push(deg + offset);
    });
    return out;
})();

export const CHIRON_FIRST_MS = START_MS + STEP_MS;
export const CHIRON_LAST_MS = START_MS + (LON.length - 2) * STEP_MS;

/** Whether the table covers this moment. */
export const chironCovers = (utcMs: number): boolean =>
    utcMs >= CHIRON_FIRST_MS && utcMs <= CHIRON_LAST_MS;

/** Chiron's ecliptic longitude (0 to 360) at a UTC time; only valid where `chironCovers`. */
export const chironLongitude = (utcMs: number): number => {
    const x = (utcMs - START_MS) / STEP_MS;
    const i = Math.min(Math.max(Math.floor(x), 1), LON.length - 3);
    const t = x - i;
    const [p0, p1, p2, p3] = [
        unwrapped[i - 1],
        unwrapped[i],
        unwrapped[i + 1],
        unwrapped[i + 2],
    ];
    // Catmull-Rom through the four neighbours.
    const v =
        p1 +
        0.5 *
            t *
            (p2 -
                p0 +
                t *
                    (2 * p0 -
                        5 * p1 +
                        4 * p2 -
                        p3 +
                        t * (3 * (p1 - p2) + p3 - p0)));
    return norm360(v);
};

export const chironRetrograde = (utcMs: number): boolean => {
    const before = chironLongitude(utcMs - 43_200_000);
    const after = chironLongitude(utcMs + 43_200_000);
    return norm360(after - before + 180) - 180 < 0;
};
